import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useUiLabels } from '../ThemeProvider.js';
import { Text } from './Text.js';
/**
 * A busy indicator that announces itself.
 *
 * `role="status"` with an accessible name, because a silent spinner tells a
 * screen-reader user nothing at all — the page simply stops responding.
 */
export function Spinner({ size = 20, onFill = false, label }) {
    const labels = useUiLabels();
    return (_jsx("span", { className: ['lh-spinner', onFill && 'lh-spinner--on-fill'].filter(Boolean).join(' '), style: { width: size, height: size }, role: "status", "aria-label": label ?? labels.loading }));
}
/**
 * A placeholder shaped like the content that is coming.
 *
 * Preferred over a centred spinner for lists and detail pages: the layout does
 * not jump when the data arrives, and the shape tells the reader what to expect.
 * Hidden from assistive technology — the surrounding region announces loading.
 */
export function Skeleton({ width = '100%', height = 16, radius = 'sm', className }) {
    return (_jsx("span", { className: ['lh-skeleton', className].filter(Boolean).join(' '), style: { width, height, borderRadius: `var(--lh-radius-${radius})`, display: 'block' }, "aria-hidden": "true" }));
}
/**
 * Nothing here yet.
 *
 * Takes a required `title` and an optional action, because "no results" with no
 * way forward is where users leave. Every list in the product renders this
 * rather than an empty container.
 */
export function EmptyState({ title, description, icon, action, compact = false, headingLevel = 'h3', }) {
    return (_jsxs("div", { className: ['lh-state', compact && 'lh-state--compact'].filter(Boolean).join(' '), children: [icon && _jsx("span", { className: "lh-state__icon", children: icon }), _jsx(Text, { variant: "subtitle", as: headingLevel, children: title }), description && (_jsx(Text, { variant: "bodySm", tone: "secondary", children: description })), action] }));
}
/**
 * Something failed.
 *
 * `role="alert"` so the failure is announced rather than silently replacing the
 * content, and the retry is part of the component so no screen can forget it.
 */
export function ErrorState({ title, description, icon, action, compact = false, headingLevel = 'h3', }) {
    return (_jsxs("div", { className: ['lh-state', compact && 'lh-state--compact'].filter(Boolean).join(' '), role: "alert", children: [icon && _jsx("span", { className: "lh-state__icon lh-state__icon--danger", children: icon }), _jsx(Text, { variant: "subtitle", as: headingLevel, children: title }), description && (_jsx(Text, { variant: "bodySm", tone: "secondary", children: description })), action] }));
}
//# sourceMappingURL=Feedback.js.map